// pages/SessionLog.js — Admin only. Tab 1: sesi login user + force-logout.
//                                   Tab 2: pengunjung share link publik (anonim).
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { MonitorSmartphone, RefreshCw, LogOut, ShieldAlert, Loader2, Users, Link2, History } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import CardList from '../components/mobile/CardList';
import DetailSheet from '../components/mobile/DetailSheet';

const REFRESH_MS = 20000;

// Teks ID/EN lokal (halaman admin tidak memakai key terjemahan global).
const T = {
  id: {
    title: 'Session Log', sub: 'Sesi login user & pengunjung share link',
    tabUsers: 'Sesi User', tabShares: 'Share Views', tabHistory: 'Riwayat Share',
    summary: (u, d, w) => `${u} user · ${d} device online (≤ ${w} menit)`,
    sharesSummary: (n, w) => `${n} pengunjung online (≤ ${w} menit)`,
    refresh: 'Muat ulang', user: 'User', role: 'Role', device: 'Device', ip: 'IP', location: 'Lokasi',
    login: 'Login', lastSeen: 'Terakhir aktif', status: 'Status', action: 'Aksi',
    activity: 'Aktivitas', noActivity: 'Baru login',
    dashboard: 'Dashboard', viewsCol: 'View', firstSeen: 'Pertama', lastViewed: 'Terakhir dilihat',
    online: 'Online', offline: 'Offline', revoked: 'Di-logout', thisDevice: 'perangkat ini',
    forceLogout: 'Force logout', empty: 'Belum ada sesi terlacak.', emptyShares: 'Belum ada pengunjung share link.',
    time: 'Waktu', emptyHistory: 'Belum ada riwayat akses.', filterAll: 'Semua link', historyCount: (n) => `${n} kunjungan`,
    confirm: (u) => `Paksa logout sesi milik "${u}"? Sesi akan berakhir pada request berikutnya (maks ~60 detik).`,
    confirmSelf: 'Ini sesi Anda sendiri — Anda akan ikut ter-logout. Lanjutkan?',
    loadErr: 'Gagal memuat data.',
    note: 'Catatan: force-logout tidak instan — berlaku pada request berikutnya dari sesi tersebut (maks ~60 detik via heartbeat).',
    noteShares: 'Pengunjung share link bersifat anonim. Untuk menghentikan akses, cabut link-nya di halaman Share Links.',
  },
  en: {
    title: 'Session Log', sub: 'User login sessions & share-link visitors',
    tabUsers: 'User Sessions', tabShares: 'Share Views', tabHistory: 'Share History',
    summary: (u, d, w) => `${u} user · ${d} device online (≤ ${w} min)`,
    sharesSummary: (n, w) => `${n} visitor(s) online (≤ ${w} min)`,
    refresh: 'Refresh', user: 'User', role: 'Role', device: 'Device', ip: 'IP', location: 'Location',
    login: 'Login', lastSeen: 'Last seen', status: 'Status', action: 'Action',
    activity: 'Activity', noActivity: 'Just logged in',
    dashboard: 'Dashboard', viewsCol: 'Views', firstSeen: 'First', lastViewed: 'Last viewed',
    online: 'Online', offline: 'Offline', revoked: 'Revoked', thisDevice: 'this device',
    forceLogout: 'Force logout', empty: 'No sessions tracked yet.', emptyShares: 'No share-link visitors yet.',
    time: 'Time', emptyHistory: 'No access history yet.', filterAll: 'All links', historyCount: (n) => `${n} visits`,
    confirm: (u) => `Force logout the session for "${u}"? It ends on the next request (up to ~60s).`,
    confirmSelf: 'This is your own session — you will be logged out too. Continue?',
    loadErr: 'Failed to load data.',
    note: 'Note: force-logout is not instant — it takes effect on the session\u2019s next request (at most ~60s via heartbeat).',
    noteShares: 'Share-link visitors are anonymous. To stop access, revoke the link on the Share Links page.',
  },
};

function relTime(ts, lang) {
  if (!ts) return '—';
  const then = new Date(String(ts).replace(' ', 'T') + 'Z').getTime();
  const sec  = Math.max(0, Math.round((Date.now() - then) / 1000));
  const u = lang === 'en'
    ? { s: 's ago', m: 'm ago', h: 'h ago' }
    : { s: ' dtk lalu', m: ' mnt lalu', h: ' jam lalu' };
  if (sec < 60) return `${sec}${u.s}`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}${u.m}`;
  return `${Math.round(min / 60)}${u.h}`;
}

const APP_NAMES = { primarydash: 'Primary Dashboard', dashmgmt: 'Dashboard Mgmt' };
function deviceLabel(ua, app) {
  if (app && /okhttp/i.test(ua || '')) return 'App ' + (APP_NAMES[app] || app) + ' · Android';
  if (!ua) return 'Unknown';
  // App mobile (React Native) memakai HTTP client okhttp, bukan browser.
  if (/okhttp/i.test(ua)) return 'Mobile app · Android';
  const os =
    /Windows/i.test(ua) ? 'Windows' :
    /iPhone|iPad/i.test(ua) ? 'iOS' :
    /Android/i.test(ua) ? 'Android' :
    /Mac OS X|Macintosh/i.test(ua) ? 'macOS' :
    /Linux/i.test(ua) ? 'Linux' : '';
  const br =
    /Edg\//i.test(ua) ? 'Edge' :
    /Chrome\//i.test(ua) ? 'Chrome' :
    /Firefox\//i.test(ua) ? 'Firefox' :
    /Safari\//i.test(ua) ? 'Safari' : 'Browser';
  return os ? `${br} · ${os}` : br;
}

const ROLE_BADGE = {
  admin: 'bg-blue-100 text-blue-700',
  SAP:   'bg-emerald-100 text-emerald-700',
  AWS:   'bg-amber-100 text-amber-700',
};

function StatusBadge({ online, revoked, tr }) {
  if (revoked) return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600">
      <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />{tr.revoked}
    </span>
  );
  if (online) return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{tr.online}
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />{tr.offline}
    </span>
  );
}


// Lokasi GPS dari app mobile (null untuk web / izin ditolak).
function locText(s) {
  if (s.loc_lat == null || s.loc_lng == null) return null;
  return Number(s.loc_lat).toFixed(4) + ', ' + Number(s.loc_lng).toFixed(4);
}
function LocLink({ s }) {
  const t = locText(s);
  if (!t) return <span className="text-slate-300">—</span>;
  return (
    <a href={'https://www.google.com/maps?q=' + s.loc_lat + ',' + s.loc_lng} target="_blank" rel="noopener noreferrer"
       className="text-brand-600 hover:underline font-mono text-xs"
       title={[s.loc_acc ? '±' + Math.round(s.loc_acc) + ' m' : '', s.loc_at ? 'update ' + new Date(String(s.loc_at).replace(' ', 'T') + 'Z').toLocaleString() : ''].filter(Boolean).join(' · ')}>{t}</a>
  );
}

export default function SessionLog() {
  const { lang, locale } = useLang();
  const tr = T[lang === 'en' ? 'en' : 'id'];

  const [tab, setTab] = useState('users'); // 'users' | 'shares'

  // ── Sesi user ──
  const [sessions, setSessions] = useState([]);
  const [windowMin, setWindowMin] = useState(5);
  const [busy, setBusy] = useState(null);

  // ── Share views ──
  const [shareViews, setShareViews] = useState([]);
  const [shareWindow, setShareWindow] = useState(5);

  // ── Riwayat share ──
  const [history, setHistory]   = useState([]);
  const [links, setLinks]       = useState([]);
  const [histToken, setHistToken] = useState(''); // '' = semua link

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const isMobile = useIsMobile();
  const [detailSess, setDetailSess] = useState(null); // session_id utk DetailSheet

  const loadUsers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/api/sessions');
      setSessions(data.sessions || []);
      setWindowMin(data.windowMinutes || 5);
      setError(null);
    } catch { setError(tr.loadErr); }
    finally { setLoading(false); }
  }, [tr.loadErr]);

  const loadShares = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/api/share-views');
      setShareViews(data.views || []);
      setShareWindow(data.windowMinutes || 5);
      setError(null);
    } catch { setError(tr.loadErr); }
    finally { setLoading(false); }
  }, [tr.loadErr]);

  const loadHistory = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/api/share-history', { params: histToken ? { t: histToken } : {} });
      setHistory(data.history || []);
      setLinks(data.links || []);
      setError(null);
    } catch { setError(tr.loadErr); }
    finally { setLoading(false); }
  }, [tr.loadErr, histToken]);

  const reload = useCallback((silent) => {
    if (tab === 'users')  return loadUsers(silent);
    if (tab === 'shares') return loadShares(silent);
    return loadHistory(silent);
  }, [tab, loadUsers, loadShares, loadHistory]);

  useEffect(() => {
    reload();
    const id = setInterval(() => reload(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  const handleRevoke = async (s) => {
    const msg = s.isCurrent ? tr.confirmSelf : tr.confirm(s.username);
    if (!window.confirm(msg)) return;
    setBusy(s.session_id);
    try { await api.post(`/api/sessions/${s.session_id}/revoke`); await loadUsers(true); }
    catch { setError(tr.loadErr); }
    finally { setBusy(null); }
  };

  const onlineCount  = sessions.filter(s => s.online).length;
  const onlineUsers  = new Set(sessions.filter(s => s.online).map(s => s.username)).size;
  const onlineShares = shareViews.filter(v => v.online).length;

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border
        ${tab === id
          ? 'bg-brand-600 text-white border-brand-600 shadow-md'
          : 'bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50'}`}
    >
      <Icon style={{ width: 15, height: 15 }} />{label}
    </button>
  );

  return (
    <div className="p-6 content-animate">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <MonitorSmartphone className="text-emerald-600" style={{ width: 22, height: 22 }} />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-800">{tr.title}</h2>
            <p className="text-sm text-slate-500">{tr.sub}</p>
            <p className="text-xs text-emerald-600 font-medium mt-1">
              {tab === 'users'
                ? tr.summary(onlineUsers, onlineCount, windowMin)
                : tab === 'shares'
                ? tr.sharesSummary(onlineShares, shareWindow)
                : tr.historyCount(history.length)}
            </p>
          </div>
        </div>
        <button
          onClick={() => reload()}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold border bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 hover:bg-brand-50 transition-all"
        >
          <RefreshCw style={{ width: 15, height: 15 }} className={loading ? 'animate-spin' : ''} />
          {tr.refresh}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <TabBtn id="users"  icon={Users} label={tr.tabUsers} />
        <TabBtn id="shares" icon={Link2} label={tr.tabShares} />
        <TabBtn id="history" icon={History} label={tr.tabHistory} />
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 font-medium">{error}</div>
      )}

      {/* ── TAB: Sesi User ── */}
      {tab === 'users' && (
        <>
          {isMobile ? (
            <div className="card p-0 overflow-hidden">
              <CardList
                items={sessions}
                keyOf={(s) => String(s.session_id)}
                title={(s) => s.username + (s.isCurrent ? ` (${tr.thisDevice})` : '')}
                badge={(s) => Number(s.revoked) === 1
                  ? { label: tr.revoked || 'Revoked', tone: 'slate' }
                  : (s.online ? { label: tr.online || 'Online', tone: 'emerald' } : { label: tr.offline || 'Offline', tone: 'slate' })}
                subtitle={(s) => [deviceLabel(s.user_agent, s.loc_app), s.ip_address, relTime(s.last_seen, lang)].filter(Boolean).join(' · ')}
                onOpen={(s) => setDetailSess(s.session_id)}
                emptyText={tr.empty}
              />
            </div>
          ) : (
          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="text-left font-semibold px-4 py-3">{tr.user}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.role}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.device}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.activity}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.ip}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.location}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.login}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.lastSeen}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.status}</th>
                    <th className="text-right font-semibold px-4 py-3">{tr.action}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sessions.length === 0 && !loading ? (
                    <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-400">{tr.empty}</td></tr>
                  ) : sessions.map((s) => {
                    const isRevoked = Number(s.revoked) === 1;
                    return (
                      <tr key={s.session_id} className="hover:bg-slate-50/60">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-700">{s.username}</div>
                          {s.isCurrent && <div className="text-[11px] text-brand-500 font-medium">({tr.thisDevice})</div>}
                        </td>
                        <td className="px-4 py-3"><span className={`role-badge ${ROLE_BADGE[s.role] || 'bg-slate-100 text-slate-600'}`}>{s.role}</span></td>
                        <td className="px-4 py-3 text-slate-600">{deviceLabel(s.user_agent, s.loc_app)}</td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700 font-medium truncate max-w-[200px]" title={s.last_activity || ''}>
                            {s.last_activity || <span className="text-slate-400 italic font-normal">{tr.noActivity}</span>}
                          </div>
                          {s.last_activity_at && <div className="text-[11px] text-slate-400">{relTime(s.last_activity_at, lang)}</div>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">{s.ip_address || '—'}</td>
                        <td className="px-4 py-3"><LocLink s={s} /></td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{s.created_at ? new Date(String(s.created_at).replace(' ', 'T') + 'Z').toLocaleString(locale) : '—'}</td>
                        <td className="px-4 py-3 text-slate-600 text-xs">{relTime(s.last_seen, lang)}</td>
                        <td className="px-4 py-3"><StatusBadge online={s.online} revoked={isRevoked} tr={tr} /></td>
                        <td className="px-4 py-3 text-right">
                          {isRevoked ? <span className="text-xs text-slate-300">—</span> : (
                            <button
                              onClick={() => handleRevoke(s)}
                              disabled={busy === s.session_id}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50 transition-all"
                            >
                              {busy === s.session_id ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" /> : <LogOut style={{ width: 13, height: 13 }} />}
                              {tr.forceLogout}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          )}
          <p className="flex items-start gap-1.5 text-xs text-slate-400 mt-4">
            <ShieldAlert style={{ width: 14, height: 14 }} className="flex-shrink-0 mt-0.5" />{tr.note}
          </p>

          {isMobile && detailSess != null && (() => {
            const s = sessions.find((x) => x.session_id === detailSess);
            if (!s) return null;
            const isRevoked = Number(s.revoked) === 1;
            return (
              <DetailSheet
                open
                title={s.username}
                badge={<StatusBadge online={s.online} revoked={isRevoked} tr={tr} />}
                fields={[
                  { label: tr.role, value: s.role },
                  { label: tr.device, value: deviceLabel(s.user_agent, s.loc_app) },
                  { label: tr.activity, value: s.last_activity },
                  { label: tr.ip, value: s.ip_address },
                  { label: tr.location, value: locText(s) ? <LocLink s={s} /> : null },
                  { label: tr.login, value: s.created_at ? new Date(String(s.created_at).replace(' ', 'T') + 'Z').toLocaleString(locale) : null },
                  { label: tr.lastSeen, value: relTime(s.last_seen, lang) },
                ]}
                actions={isRevoked ? [] : [{
                  key: 'revoke', label: tr.forceLogout, Icon: LogOut, tone: 'rose',
                  busy: busy === s.session_id,
                  onClick: async () => { await handleRevoke(s); setDetailSess(null); },
                }]}
                onClose={() => setDetailSess(null)}
              />
            );
          })()}
        </>
      )}

      {/* ── TAB: Share Views ── */}
      {tab === 'shares' && (
        <>
          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="text-left font-semibold px-4 py-3">{tr.dashboard}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.ip}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.device}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.viewsCol}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.firstSeen}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.lastViewed}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.status}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {shareViews.length === 0 && !loading ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">{tr.emptyShares}</td></tr>
                  ) : shareViews.map((v, i) => (
                    <tr key={i} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 font-semibold text-slate-700">{v.label || v.dashboard_key}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{v.ip || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{deviceLabel(v.user_agent)}</td>
                      <td className="px-4 py-3 text-slate-700 font-semibold">{v.views}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{v.first_seen ? new Date(String(v.first_seen).replace(' ', 'T') + 'Z').toLocaleString(locale) : '—'}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{relTime(v.last_seen, lang)}</td>
                      <td className="px-4 py-3"><StatusBadge online={v.online} revoked={false} tr={tr} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-slate-400 mt-4">
            <ShieldAlert style={{ width: 14, height: 14 }} className="flex-shrink-0 mt-0.5" />{tr.noteShares}
          </p>
        </>
      )}

      {/* ── TAB: Riwayat Share ── */}
      {tab === 'history' && (
        <>
          <div className="mb-3">
            <select
              value={histToken}
              onChange={(e) => setHistToken(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 outline-none focus:border-brand-400"
            >
              <option value="">{tr.filterAll}</option>
              {links.map((l) => (
                <option key={l.token} value={l.token}>
                  {(l.label || l.dashboard_key)} · …{String(l.token).slice(-6)}
                </option>
              ))}
            </select>
          </div>
          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                    <th className="text-left font-semibold px-4 py-3">{tr.time}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.dashboard}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.ip}</th>
                    <th className="text-left font-semibold px-4 py-3">{tr.device}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.length === 0 && !loading ? (
                    <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">{tr.emptyHistory}</td></tr>
                  ) : history.map((h, i) => (
                    <tr key={i} className="hover:bg-slate-50/60">
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">
                        {h.accessed_at ? new Date(String(h.accessed_at).replace(' ', 'T') + 'Z').toLocaleString(locale) : '—'}
                        <span className="text-slate-400"> · {relTime(h.accessed_at, lang)}</span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">{h.label || h.dashboard_key}</td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs">{h.ip || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{deviceLabel(h.user_agent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-xs text-slate-400 mt-4">
            <ShieldAlert style={{ width: 14, height: 14 }} className="flex-shrink-0 mt-0.5" />{tr.noteShares}
          </p>
        </>
      )}
    </div>
  );
}
